/**
 * Programme Dates Section Component
 * Collapsible section for tracking project timeline
 * CHANGE 1: Two-way calculation between Start Date, Duration, and Completion Date
 */
import { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Calendar, Edit, Save, X, Loader2, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import { format, parseISO, addWeeks, differenceInWeeks } from 'date-fns';

export function ProgrammeDatesSection({ 
  tender, 
  canEdit, 
  isArchived, 
  onSave,
  submitting 
}) {
  const [expanded, setExpanded] = useState(true); // Changed to expanded by default
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [lastChanged, setLastChanged] = useState(null);

  const startEdit = () => {
    setEditData({
      proposed_start_date: tender.programme_dates?.proposed_start_date || '',
      estimated_duration_weeks: tender.programme_dates?.estimated_duration_weeks?.toString() || '',
      proposed_completion_date: tender.programme_dates?.proposed_completion_date || '',
      notes: tender.programme_dates?.notes || ''
    });
    setEditMode(true);
    setLastChanged(null);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditData({});
    setLastChanged(null);
  };

  // Two-way calculation logic (CHANGE 1)
  useEffect(() => {
    if (!editMode) return;
    
    const startDate = editData.proposed_start_date;
    const duration = editData.estimated_duration_weeks;
    const completionDate = editData.proposed_completion_date;
    
    if (lastChanged === 'start' || lastChanged === 'duration') {
      // Calculate completion date from start + duration
      if (startDate && duration) {
        try {
          const start = parseISO(startDate);
          const weeks = parseInt(duration, 10);
          if (!isNaN(weeks) && weeks > 0) {
            const completion = addWeeks(start, weeks);
            setEditData(prev => ({
              ...prev,
              proposed_completion_date: format(completion, 'yyyy-MM-dd')
            }));
          }
        } catch (e) {
          console.error('Date calculation error (start/duration):', e);
        }
      }
    } else if (lastChanged === 'completion') {
      // Calculate duration from start and completion
      if (startDate && completionDate) {
        try {
          const start = parseISO(startDate);
          const completion = parseISO(completionDate);
          const weeks = differenceInWeeks(completion, start);
          if (weeks >= 0) {
            setEditData(prev => ({
              ...prev,
              estimated_duration_weeks: Math.ceil(weeks).toString()
            }));
          }
        } catch (e) {
          console.error('Date calculation error (completion):', e);
        }
      }
    }
  }, [editData.proposed_start_date, editData.estimated_duration_weeks, editData.proposed_completion_date, lastChanged, editMode]);

  const handleStartDateChange = (e) => {
    setEditData(prev => ({ ...prev, proposed_start_date: e.target.value }));
    setLastChanged('start');
  };

  const handleDurationChange = (e) => {
    setEditData(prev => ({ ...prev, estimated_duration_weeks: e.target.value }));
    setLastChanged('duration');
  };

  const handleCompletionDateChange = (e) => {
    setEditData(prev => ({ ...prev, proposed_completion_date: e.target.value }));
    setLastChanged('completion');
  };

  const handleSave = async () => {
    const data = {
      proposed_start_date: editData.proposed_start_date || null,
      estimated_duration_weeks: editData.estimated_duration_weeks ? parseInt(editData.estimated_duration_weeks) : null,
      proposed_completion_date: editData.proposed_completion_date || null,
      notes: editData.notes || null
    };
    
    const success = await onSave(data);
    if (success) {
      setEditMode(false);
      setEditData({});
      setLastChanged(null);
    }
  };

  return (
    <Card className="bg-[#2A3240] border-white/10 mt-6">
      <CardHeader 
        className="pb-3 cursor-pointer hover:bg-white/5 transition-colors rounded-t-lg"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <Calendar className="w-5 h-5 text-[#F5A623]" />
            Programme
          </CardTitle>
          <div className="flex items-center gap-2">
            {tender.programme_dates?.estimated_duration_weeks && (
              <Badge className="bg-[#F5A623]/20 text-[#F5A623] border-[#F5A623]/30 text-xs">
                {tender.programme_dates.estimated_duration_weeks} weeks
              </Badge>
            )}
            {tender.programme_dates?.proposed_start_date && (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                Start: {format(parseISO(tender.programme_dates.proposed_start_date), 'dd MMM yyyy')}
              </Badge>
            )}
            {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <p className="text-gray-400 text-sm">
              {editMode 
                ? 'Enter Start Date + Duration to auto-calculate Completion, or enter Completion directly to recalculate Duration.'
                : 'Track proposed project timeline and duration'
              }
            </p>
            {!isArchived && canEdit && !editMode && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); startEdit(); }}
                className="text-gray-400 hover:text-[#F5A623] h-7"
                data-testid="edit-programme-btn"
              >
                <Edit className="w-3 h-3 mr-1" />
                Edit
              </Button>
            )}
            {editMode && (
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={cancelEdit}
                  className="text-gray-400 hover:text-white h-7"
                >
                  <X className="w-3 h-3 mr-1" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={submitting}
                  className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] h-7"
                  data-testid="save-programme-btn"
                >
                  {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Save className="w-3 h-3 mr-1" />Save</>}
                </Button>
              </div>
            )}
          </div>

          {editMode ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300 text-sm">Start Date</Label>
                <Input
                  type="date"
                  value={editData.proposed_start_date || ''}
                  onChange={handleStartDateChange}
                  className="bg-[#1E2530] border-white/20 text-white"
                  data-testid="programme-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300 text-sm">Duration (weeks)</Label>
                <Input
                  type="number"
                  min="1"
                  value={editData.estimated_duration_weeks || ''}
                  onChange={handleDurationChange}
                  className="bg-[#1E2530] border-white/20 text-white"
                  placeholder="12"
                  data-testid="programme-duration"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300 text-sm">Completion Date</Label>
                <Input
                  type="date"
                  value={editData.proposed_completion_date || ''}
                  onChange={handleCompletionDateChange}
                  className="bg-[#1E2530] border-white/20 text-white"
                  data-testid="programme-completion-date"
                />
              </div>
              <div className="space-y-2 md:col-span-3">
                <Label className="text-gray-300 text-sm">Notes</Label>
                <Textarea
                  value={editData.notes || ''}
                  onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                  className="bg-[#1E2530] border-white/20 text-white resize-none"
                  rows={2}
                  placeholder="Add any notes about the programme..."
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-3 bg-[#1E2530] rounded-lg border border-white/10">
                <Label className="text-gray-500 text-xs uppercase">Start Date</Label>
                <p className="text-white mt-1 font-medium">
                  {tender.programme_dates?.proposed_start_date 
                    ? format(parseISO(tender.programme_dates.proposed_start_date), 'dd MMM yyyy')
                    : '—'}
                </p>
              </div>
              <div className="p-3 bg-[#1E2530] rounded-lg border border-white/10">
                <Label className="text-gray-500 text-xs uppercase">Duration</Label>
                <p className="text-white mt-1 font-medium flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-[#F5A623]" />
                  {tender.programme_dates?.estimated_duration_weeks 
                    ? `${tender.programme_dates.estimated_duration_weeks} weeks`
                    : '—'}
                </p>
              </div>
              <div className="p-3 bg-[#1E2530] rounded-lg border border-white/10">
                <Label className="text-gray-500 text-xs uppercase">Completion Date</Label>
                <p className="text-white mt-1 font-medium">
                  {tender.programme_dates?.proposed_completion_date 
                    ? format(parseISO(tender.programme_dates.proposed_completion_date), 'dd MMM yyyy')
                    : '—'}
                </p>
              </div>
              {tender.programme_dates?.notes && (
                <div className="md:col-span-3 p-3 bg-[#1E2530] rounded-lg border border-white/10">
                  <Label className="text-gray-500 text-xs uppercase">Notes</Label>
                  <p className="text-gray-300 mt-1 text-sm whitespace-pre-wrap">{tender.programme_dates.notes}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
